import { FormControl, MenuItem, Paper, Stack } from "@mui/material";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect } from "react";
import {
  CustomTextField,
  CustomToggleCompoment,
  CustomTagComponent,
  CustomSelectComponent,
} from "../../../ui/input";
import { CustomInputLabel, CustomTypographyComponent } from "../../../ui/text";
import debounce from "lodash.debounce";
import { ArrayFormSchema, ArrayUiSchema } from "../types";

interface StateValue {
  formSchema: ArrayFormSchema;
  uiSchema: ArrayUiSchema;
  name: string;
}

interface FormArrayViewerProps {
  inputPropertyData: any;
  onFormStateChange: (formState) => void;
}

const getFuncTypeArray = (arrayObject: any): string => {
  if (arrayObject && arrayObject.uniqueItems) {
    return "array";
  } else if (arrayObject && arrayObject.uiField) {
    return "image";
  } else {
    return "lsitOfString";
  }
};

const FormArrayViewer = ({
  inputPropertyData,
  onFormStateChange,
}: FormArrayViewerProps) => {
  const [formPropertiesState, setFormPropertiesState] =
    React.useState<StateValue>(null);

  const getTypeArray = getFuncTypeArray(inputPropertyData);

  const t = useTranslations();

  useEffect(() => {
    setFormPropertiesState({
      ...inputPropertyData,
    });
  }, [inputPropertyData]);

  const debouncedSave = useCallback(
    debounce((nextValue) => onFormStateChange(nextValue), 1000),
    [onFormStateChange] // will be created only once initially
  );

  const handleChangeFormSchema =
    (prop: keyof StateValue["formSchema"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormPropertiesState((prevState: StateValue) => {
        debouncedSave({
          ...prevState,
          formSchema: { ...prevState.formSchema, [prop]: event.target.value },
        });
        return {
          ...prevState,
          formSchema: { ...prevState.formSchema, [prop]: event.target.value },
        };
      });
    };

  const handleChangeUISchema =
    (prop: keyof StateValue["uiSchema"]["ui:options"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const prevUiOptions =
        (formPropertiesState.uiSchema &&
          formPropertiesState.uiSchema["ui:options"]) ||
        null;
      const fieldValue =
        event.target.value === "on" || event.target.value === "off"
          ? event.target.checked
          : event.target.value;
      //@ts-ignore
      setFormPropertiesState((prevState: StateValue) => {
        debouncedSave({
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,

            "ui:options": {
              ...prevUiOptions,
              [prop]: fieldValue,
            },
          },
        });
        return {
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,
            "ui:options": {
              ...prevUiOptions,
              [prop]: fieldValue,
            },
          },
        };
      });
    };

  const handleTagUpdate = (tagValue: string[]) => {
    const newItems = {
      type: formPropertiesState.formSchema.items.type,
      enum: tagValue,
    };
    //@ts-ignore
    setFormPropertiesState((prevState: StateValue) => {
      debouncedSave({
        ...prevState,
        formSchema: {
          ...prevState.formSchema,
          items: newItems,
        },
      });
      return {
        ...prevState,
        formSchema: {
          ...prevState.formSchema,
          items: newItems,
        },
      };
    });
  };

  const handleItemTypeUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItems = {
      type: event.target.value,
      enum: formPropertiesState.formSchema.items,
    };

    //@ts-ignore
    setFormPropertiesState((prevState: StateValue) => {
      debouncedSave({
        ...prevState,
        formSchema: {
          ...prevState.formSchema,
          items: newItems,
        },
      });
      return {
        ...prevState,
        formSchema: {
          ...prevState.formSchema,
          items: newItems,
        },
      };
    });
  };

  const handleRenderFormSchema = (item) => (
    <Stack padding={3} spacing={2} direction="column" height={250} width={320}>
      <FormControl sx={{ width: 300 }}>
        <CustomTextField
          controldisabled={false}
          id="outlined-adornment-title"
          value={
            (formPropertiesState && formPropertiesState.formSchema.title) || ""
          }
          label={t("labels.title")}
          onChange={handleChangeFormSchema("title")}
        />
      </FormControl>

      {getTypeArray === "array" && (
        <FormControl id="outlined-adornment-tag" sx={{ width: "100%" }}>
          <CustomTagComponent
            // controldisabled={false}
            maxHeight={80}
            inputValue={
              (formPropertiesState &&
                formPropertiesState.formSchema.items &&
                //@ts-ignore
                formPropertiesState.formSchema.items.enum) ||
              []
            }
            label={t("labels.enum")}
            onTagValueChange={handleTagUpdate}
          />
        </FormControl>
      )}
      {getTypeArray === "array" && (
        <FormControl sx={{ width: 300 }}>
          <CustomInputLabel id="type-select-label">
            {t("labels.type")}
          </CustomInputLabel>
          <CustomSelectComponent
            labelId="type-select-label"
            id="type-select"
            controldisabled={false}
            size="small"
            value={
              formPropertiesState &&
              formPropertiesState.formSchema &&
              formPropertiesState.formSchema.items.type
            }
            label={t("labels.type")}
            onChange={handleItemTypeUpdate}
          >
            <MenuItem value={"number"}>{t("labels.number")}</MenuItem>

            <MenuItem value={"string"}>{t("labels.string")}</MenuItem>
          </CustomSelectComponent>
        </FormControl>
      )}
    </Stack>
  );

  const renderUIOptions = (item) => (
    <Stack padding={3} spacing={2} direction="column" height={280} width={500}>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("fullWidth")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].fullWidth) ||
            false
          }
          label={t("labels.fullWidth")}
          name={t("labels.fullWidth")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("newLine")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].newLine) ||
            false
          }
          label={t("labels.newLine")}
          name={t("lables.newLine")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("autofocus")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].autofocus) ||
            false
          }
          label={t("labels.autofocus")}
          name={t("labels.autofocus")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("hideInApp")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].hideInApp) ||
            false
          }
          label={t("labels.hideInApp")}
          name={t("labels.hideInApp")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("inline")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].inline) ||
            false
          }
          label={t("labels.inline")}
          name={t("labels.inline")}
        />
      </FormControl>

      {/* <FormControl  sx={{ width: 300 }}>
        <CustomInputLabel id="widget-select-format">
          {t("uiWidget")}
        </CustomInputLabel>
        <CustomSelectComponent
          labelId="widget-select-label"
          id="widget-select"
          controldisabled={false}
          size="small"
          value={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:widget"]) ||
            ""
          }
          label={t("uiWidget")}
          onChange={handleUiWidget}
        >
          <MenuItem value={""}>{t("default")}</MenuItem>
          <MenuItem value={"RadioWidget"}>{t("radioButton")}</MenuItem>
          <MenuItem value={"SegmentedButtonWidget"}>
            {t("SegmentedButtonWidget")}
          </MenuItem>
        </CustomSelectComponent>
      </FormControl> */}
    </Stack>
  );
  return (
    <Stack id="FormBuilderPropertiesView">
      <Paper>
        <Stack spacing={2}>
          <Stack mb={3}>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labels.formSchema")}
              </CustomTypographyComponent>
              <CustomTypographyComponent variant="h6">
                {formPropertiesState && formPropertiesState.name}
              </CustomTypographyComponent>
            </Stack>
            {formPropertiesState && handleRenderFormSchema(formPropertiesState)}
          </Stack>
          <Stack>
            <Stack mt={10} alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labels.uiSchema")}
              </CustomTypographyComponent>
            </Stack>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="body2">
                {t("lables.uiOptions")}
              </CustomTypographyComponent>
            </Stack>

            {formPropertiesState && renderUIOptions(formPropertiesState)}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default FormArrayViewer;
